import type { MouseEvent, ReactNode } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl, type IntlShape } from 'react-intl'

import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Separator from 'LuxKit/Separator'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import FormatCurrency from 'components/Common/FormatCurrency'
import getVoucherProductTypeIcon from 'lib/voucher/getVoucherProductTypeIcon'
import getVoucherThemeClassName from 'lib/voucher/getVoucherThemeClassName'
import getVoucherTitleColour from 'lib/voucher/getVoucherTitleColour'

const MOBILE_NOTCH_DIAMETER = 12
const MOBILE_NOTCH_RADIUS = MOBILE_NOTCH_DIAMETER / 2
const MOBILE_NOTCH_X = 56

const DESKTOP_NOTCH_DIAMETER = 16
const DESKTOP_NOTCH_RADIUS = DESKTOP_NOTCH_DIAMETER / 2
const DESKTOP_NOTCH_X = 64

const VoucherCardMobile = styled(Group)`
  position: relative;
  padding-block: ${rem(12)};
  padding-inline: ${rem(12)} ${rem(10)};
  width: ${rem(300)};
  height: ${rem(80)};
  flex-shrink: 0;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  clip-path: inset(0 round ${rem(8)});

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: ${rem(MOBILE_NOTCH_DIAMETER)};
    height: ${rem(MOBILE_NOTCH_DIAMETER)};
    inset-inline-start: ${rem(MOBILE_NOTCH_X)};
    transform: translateX(-50%);
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    z-index: 1;

    [dir="rtl"] & {
      transform: translateX(50%);
    }
  }

  &::before {
    top: -${rem(MOBILE_NOTCH_RADIUS)};
    border-radius: ${rem(MOBILE_NOTCH_DIAMETER)};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &::after {
    bottom: -${rem(MOBILE_NOTCH_RADIUS)};
    border-radius: ${rem(MOBILE_NOTCH_DIAMETER)};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const VoucherCardDesktop = styled(Group)`
  position: relative;
  padding: ${rem(12)} ${rem(16)};
  width: ${rem(420)};
  height: ${rem(100)};
  flex-shrink: 0;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  clip-path: inset(0 round ${rem(12)});

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: ${rem(DESKTOP_NOTCH_DIAMETER)};
    height: ${rem(DESKTOP_NOTCH_DIAMETER)};
    inset-inline-start: ${rem(DESKTOP_NOTCH_X)};
    transform: translateX(-50%);
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    z-index: 1;

    [dir="rtl"] & {
      transform: translateX(50%);
    }
  }

  &::before {
    top: -${rem(DESKTOP_NOTCH_RADIUS)};
    border-radius: ${rem(DESKTOP_NOTCH_DIAMETER)};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &::after {
    bottom: -${rem(DESKTOP_NOTCH_RADIUS)};
    border-radius: ${rem(DESKTOP_NOTCH_DIAMETER)};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${props => props.theme.palette.highlight.primary.darkBackground};

  &.theme-luxplus {
    background-color: ${props => props.theme.palette.product.luxPlus.foreground};
  }

  &.theme-societe {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
  }

  &.theme-societe-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
  }

  &.theme-societe-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
  }

  &.theme-societe-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
  }

  &.theme-societe-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
  }
`

const TextWrapper = styled(Group)`
  min-width: 0;
`

const MobileDottedDivider = styled.div`
  height: ${rem(56)};
  width: 0;
  flex-shrink: 0;
  border-inline-start: 2px dashed ${props => props.theme.palette.neutral.surface.lightForeground};
`

const DesktopDottedDivider = styled.div`
  height: ${rem(76)};
  width: 0;
  flex-shrink: 0;
  border-inline-start: 2px dashed ${props => props.theme.palette.neutral.surface.lightForeground};
`

export interface VoucherCarouselCardVoucher {
  voucherName: string
  voucherTitle?: string
  voucherDescription?: string
  voucherAmountDisplay?: string | null
  voucherDisplayAmount: number | null
  theme?: App.VoucherTheme
  productType?: App.VoucherProductType
}

interface Props {
  variant: 'mobile' | 'desktop'
  voucher: VoucherCarouselCardVoucher
  expiryText?: string
  rightSlot?: ReactNode
  onDetailsClick?: () => void
  className?: string
}

export function renderVoucherCarouselCardValue(voucher: VoucherCarouselCardVoucher, intl: IntlShape): ReactNode {
  if (voucher.voucherAmountDisplay !== undefined && voucher.voucherAmountDisplay !== '') {
    return voucher.voucherAmountDisplay
  }
  if (voucher.voucherDisplayAmount != null) {
    return <FormatCurrency value={voucher.voucherDisplayAmount} />
  }
  return intl.formatMessage({
    id: '1QjDXoywCNOij-FlfD9Y',
    defaultMessage: 'Voucher',
    description: 'Form field label for voucher when amount is not specified',
  })
}

function VoucherCarouselCard({ variant, voucher, expiryText, rightSlot, onDetailsClick, className }: Props) {
  const intl = useIntl()
  const VoucherIcon = getVoucherProductTypeIcon(voucher.productType)
  const titleColour = getVoucherTitleColour(voucher.theme)
  const title = voucher.voucherTitle || voucher.voucherName
  const hasDetails = Boolean(voucher.voucherDescription) && Boolean(onDetailsClick)
  const themeClass = getVoucherThemeClassName(voucher.theme)

  const handleDetailsClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    onDetailsClick?.()
  }

  const detailsLabel = intl.formatMessage({
    id: 'UT4BQ3uROjMjPlVz42pB',
    defaultMessage: 'Details',
    description: 'Link text to view voucher details in the available rewards section',
  })

  if (variant === 'mobile') {
    return (
      <VoucherCardMobile className={className} direction="horizontal" gap={12} verticalAlign="center">
        <IconCircle className={themeClass}>
          <VoucherIcon size={20} colour="dark-contrast" />
        </IconCircle>
        <MobileDottedDivider />
        <TextWrapper direction="vertical" gap={2} fullWidth>
          <BodyText variant="small" colour={titleColour} wrap="truncate">{title}</BodyText>
          <BodyText variant="medium" weight="bold" colour="light-foreground-alt" wrap="truncate">
            {renderVoucherCarouselCardValue(voucher, intl)}
          </BodyText>
          {(expiryText || hasDetails) && <Group direction="horizontal" gap={4} verticalAlign="center">
            {expiryText && <Caption variant="medium" colour="light-foreground-alt">{expiryText}</Caption>}
            {expiryText && hasDetails && <Separator variant="middot" />}
            {hasDetails && <Clickable onClick={handleDetailsClick}>
              <Caption variant="medium" colour="light-foreground-alt">
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <Caption variant="medium" colour="light-foreground-alt" underline>
                    {detailsLabel}
                  </Caption>
                  <LineInfoCircleIcon size={16} colour="light-foreground-alt" />
                </Group>
              </Caption>
            </Clickable>}
          </Group>}
        </TextWrapper>
        {rightSlot}
      </VoucherCardMobile>
    )
  }

  return (
    <VoucherCardDesktop className={className} direction="horizontal" gap={16} verticalAlign="center">
      <IconCircle className={themeClass}>
        <VoucherIcon size={20} colour="dark-contrast" />
      </IconCircle>
      <DesktopDottedDivider />
      <TextWrapper direction="vertical" gap={4} fullWidth>
        <BodyText variant="medium" colour={titleColour} wrap="truncate">{title}</BodyText>
        <BodyText variant="large" weight="bold" colour="light-foreground-alt" wrap="truncate">
          {renderVoucherCarouselCardValue(voucher, intl)}
        </BodyText>
        {(expiryText || hasDetails) && <Group direction="horizontal" gap={4} verticalAlign="center">
          {expiryText && <Caption variant="medium" colour="light-foreground-alt">{expiryText}</Caption>}
          {expiryText && hasDetails && <Separator variant="middot" />}
          {hasDetails && <Clickable onClick={handleDetailsClick}>
            <Caption variant="medium" colour="light-foreground-alt" underline>
              {detailsLabel}
            </Caption>
          </Clickable>}
        </Group>}
      </TextWrapper>
      {rightSlot}
    </VoucherCardDesktop>
  )
}

export default VoucherCarouselCard
